import React from "react";
import { AvailabilityAttachment } from "../types/ghlBookingChat";

interface AvailabilityCardProps {
  data: AvailabilityAttachment;
}

const headerIconStyle = (problem: boolean): React.CSSProperties =>
  problem
    ? { background: "color-mix(in srgb, var(--color-danger) 10%, transparent)" }
    : { background: "color-mix(in srgb, var(--color-primary) 10%, transparent)" };

const headerIconColor = (problem: boolean) =>
  problem ? "text-[color:var(--color-danger)]" : "text-theme-primary";

const badgeStyle: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-danger) 12%, var(--color-surface))",
  color: "var(--color-danger)",
};

const BADGE_LABEL: Record<string, string> = {
  not_connected: "Not Connected",
  needs_reauth: "Needs Reconnect",
  no_calendar: "No Calendar",
  calendar_not_found: "Calendar Not Found",
  calendar_ambiguous: "Which Calendar?",
  unavailable: "Unavailable",
  invalid_date: "Unavailable",
};

const DEFAULT_MESSAGE: Record<string, string> = {
  not_connected: "This business hasn't connected a calendar yet.",
  needs_reauth: "The calendar connection needs to be reconnected.",
  no_calendar: "No active calendar is set up for this business yet.",
  calendar_not_found:
    "That calendar wasn't found on this account. Check the name and try again.",
  calendar_ambiguous:
    "That name matches more than one calendar — say which one you mean.",
  unavailable: "Availability lookup failed. Try again shortly.",
  invalid_date: "That date couldn't be checked. Try again shortly.",
};

const formatDate = (dateStr: string) => {
  const parts = dateStr.split("-").map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return "the requested day";
  const [y, m, d] = parts;
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return "the requested day";
  return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};

/** GHL returns slots as offset-bearing ISO strings ("...T09:00:00-05:00").
 *  `timeZone` is what keeps them rendering in the BUSINESS's zone rather than
 *  the viewer's — without it a US/Central 9:00 AM slot shows as 10:00 PM to a
 *  UTC+8 viewer, contradicting the assistant's own text answer on screen.
 *  Falls back to the viewer's locale when the backend sent no timezone (it
 *  degrades to UTC when the GHL token lacks locations.readonly). */
const formatSlot = (iso: string, timeZone?: string) => {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "—";
  const opts: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
  if (timeZone) {
    try {
      return dt.toLocaleTimeString(undefined, { ...opts, timeZone });
    } catch {
      // An unrecognised IANA name would otherwise throw RangeError and blank
      // the whole card; the viewer-local time is wrong but readable.
      return dt.toLocaleTimeString(undefined, opts);
    }
  }
  return dt.toLocaleTimeString(undefined, opts);
};

/** "US/Central" -> "Central" for the slot-list caption. Keeps the raw name
 *  when it isn't a Region/City id (e.g. "UTC"). */
const shortZone = (timeZone?: string) =>
  timeZone ? timeZone.split("/").pop()!.replace(/_/g, " ") : "";

/** Renders ghl_get_availability's result — real open slots, or an
 *  unmistakable "not available" state for every non-"ok" status. */
export const AvailabilityCard: React.FC<AvailabilityCardProps> = ({ data }) => {
  const { result } = data;
  const problem = result.status !== "ok";

  return (
    <div className="my-1 w-full max-w-[420px] overflow-hidden rounded-2xl border border-theme-border bg-theme-surface">
      <div className="flex items-center justify-between gap-2 border-b border-theme-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl"
            style={headerIconStyle(problem)}
          >
            <span className={`material-icons-outlined text-[18px] ${headerIconColor(problem)}`}>
              {problem ? "event_busy" : "event_available"}
            </span>
          </span>
          <p className="truncate text-[13px] font-bold text-theme-fg">
            {problem ? "Availability unavailable" : `Availability — ${formatDate(result.date)}`}
          </p>
        </div>
        {problem && (
          <span
            className="flex-shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wide"
            style={badgeStyle}
          >
            {BADGE_LABEL[result.status] ?? "Unavailable"}
          </span>
        )}
      </div>

      {result.status === "ok" && result.has_availability && (
        <div className="p-3">
          <p className="mb-2 px-1 text-[11.5px] text-theme-fg-muted">
            {result.duration_minutes} min appointment
            {result.calendar_name ? ` · ${result.calendar_name}` : ""}
            {result.timezone ? ` · times in ${shortZone(result.timezone)}` : ""}
          </p>
          <div className="flex flex-wrap gap-2 px-1">
            {result.available_slots.map((iso, i) => (
              <span
                key={`${iso}-${i}`}
                className="inline-flex items-center rounded-full border border-theme-border bg-theme-background px-3.5 py-1.5 text-[12.5px] font-semibold text-theme-fg"
              >
                {formatSlot(iso, result.timezone)}
              </span>
            ))}
          </div>
        </div>
      )}

      {result.status === "ok" && !result.has_availability && (
        <div className="px-4 py-6 text-center">
          <span className="material-icons-outlined mb-1 block text-[22px] text-theme-fg-muted">event_busy</span>
          <p className="text-[12.5px] text-theme-fg-muted">
            No open times on {formatDate(result.date)}. Ask for another day.
          </p>
        </div>
      )}

      {problem && (
        <div className="px-4 py-6 text-center">
          <span className="material-icons-outlined mb-1 block text-[22px] text-theme-fg-muted">
            {result.status === "needs_reauth" ? "sync_problem" : "link_off"}
          </span>
          <p className="text-[12.5px] text-theme-fg-muted">
            {result.message || DEFAULT_MESSAGE[result.status] || DEFAULT_MESSAGE.unavailable}
          </p>
          {/* No `status !== "ok"` guard: this block only renders when `problem`
              is true, so the union is already narrowed and the comparison
              would be a TS2367 no-overlap error. */}
          {result.matching_calendars?.length ? (
            <div className="mt-2.5 flex flex-wrap justify-center gap-1.5">
              {result.matching_calendars.map((name) => (
                <span
                  key={name}
                  className="inline-flex items-center rounded-full border border-theme-border bg-theme-background px-2.5 py-1 text-[11.5px] font-semibold text-theme-fg"
                >
                  {name}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
